/**
 * LoanDrilldownLiveBar — collaborative-awareness strip under the drilldown tabs.
 *
 * Two live signals for the open loan, both off the `deal:{id}` Web PubSub group
 * (see useDealLiveEvents):
 *
 *   • PRESENCE — "who else has this loan open right now": an avatar stack + names,
 *     plus a toast when someone opens the loan while you're in it. (Lights up once
 *     the backend broadcasts presence_* events; inert until then.)
 *
 *   • UPDATE PROMPT — when the loan changes elsewhere, a NON-destructive banner
 *     offering a manual Refresh (never auto-refreshes — you might have unsaved
 *     edits). Driven by the backend's existing `deal_updated` broadcast.
 *
 * Renders nothing when there are no other viewers and no pending update, so it
 * has zero footprint on a quiet loan. Self-contained + easy to remove (one mount
 * in LoanDrilldownScreen).
 */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";

import { useDealLiveEvents } from "../../../realtime/useDealLiveEvents";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";

// Warm pastel avatar palette (solid fill + dark ink) matching the presence
// banner design — each viewer gets a stable color hashed off their email.
const AVATAR_COLORS = [
  { bg: "#AFA9EC", fg: "#26215C" }, // lavender
  { bg: "#5DCAA5", fg: "#04342C" }, // green
  { bg: "#F0B49B", fg: "#5C2A14" }, // terracotta
  { bg: "#E6B3D4", fg: "#56203F" }, // pink
  { bg: "#9AC6E8", fg: "#143A57" }, // blue
  { bg: "#E7C982", fg: "#574014" }, // gold
];
const avatarColor = (key) => {
  let h = 0;
  for (let i = 0; i < (key || "").length; i += 1)
    h = (h * 31 + key.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const initials = (name) =>
  (name || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const displayName = (v) => v.name || (v.email || "").split("@")[0] || v.email;

// "X and Y are also viewing this loan" with the viewer names bolded, per the
// presence banner design.
function ViewerSentence({ viewers }) {
  const Strong = ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 600 }}>
      {children}
    </Box>
  );
  const names = viewers.map(displayName);
  if (names.length === 1) {
    return (
      <>
        <Strong>{names[0]}</Strong> is also viewing this loan
      </>
    );
  }
  if (names.length === 2) {
    return (
      <>
        <Strong>{names[0]}</Strong> and <Strong>{names[1]}</Strong> are also
        viewing this loan
      </>
    );
  }
  const others = names.length - 2;
  return (
    <>
      <Strong>{names[0]}</Strong>, <Strong>{names[1]}</Strong> and {others}{" "}
      other
      {others > 1 ? "s" : ""} are also viewing this loan
    </>
  );
}

function AvatarStack({ viewers }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {viewers.slice(0, 4).map((v, i) => {
        const c = avatarColor(v.email);
        return (
          <Tooltip key={v.email} title={v.name || v.email}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: c.bg,
                color: c.fg,
                border: "1px solid #fff",
                ml: i === 0 ? 0 : "-8px",
                fontSize: 9,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {initials(v.name || v.email)}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

const changeLabel = (change) => {
  if (!change) return "";
  const kindWord = {
    comment: "a new comment",
    document: "a document",
    condition: "a condition",
    exception: "an exception",
    task: "a task",
    stage: "the loan stage",
    lock: "the loan lock",
    loan: "loan details",
  };
  return kindWord[change.kind] || "this loan";
};

export default function LoanDrilldownLiveBar() {
  const dealId = useSelector((s) => s.loanDrilldown?._id);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { getLoanById } = useUnderwritingHook();

  const { viewers, lastJoin, clearLastJoin, update, dismissUpdate } =
    useDealLiveEvents(dealId);

  // Toast when someone opens the loan while you're in it.
  useEffect(() => {
    if (!lastJoin) return;
    enqueueSnackbar(`${lastJoin.name || lastJoin.email} opened this loan`, {
      variant: "info",
      autoHideDuration: 4000,
    });
    clearLastJoin();
  }, [lastJoin, enqueueSnackbar, clearLastJoin]);

  const handleRefresh = () => {
    if (!dealId) return;
    getLoanById({
      loanId: dealId,
      onSuccessFn: (data) => dispatch(loanDrilldownSet(data)),
    });
    dismissUpdate();
  };

  const hasViewers = viewers.length > 0;
  const hasUpdate = !!update;
  if (!hasViewers && !hasUpdate) return null;

  const high = update?.impact === "high";

  return (
    <Box sx={{ flexShrink: 0 }}>
      {hasViewers && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.25,
            background: "#faeeda",
            borderBottom: "1px solid #FAC775",
          }}
        >
          <AvatarStack viewers={viewers} />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              color: "#854f0b",
              lineHeight: 1.4,
            }}
          >
            <ViewerSentence viewers={viewers} />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.625,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#0f6e56",
                animation: "liveBarPulse 2s ease-in-out infinite",
                "@keyframes liveBarPulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.35 },
                },
              }}
            >
              {/* pulsing presence dot */}
            </Box>
            <Box sx={{ fontSize: 12, color: "#5f5e5a" }}>Live</Box>
          </Box>
        </Box>
      )}

      {hasUpdate && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 0.75,
            background: high ? "#fff7ed" : "#f8fafc",
            borderBottom: `1px solid ${high ? "#fed7aa" : "#e2e8f0"}`,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: high ? "#ea580c" : "#0a66ff",
            }}
          />
          <Box sx={{ fontSize: 12.5, color: "#334155" }}>
            This loan was just updated
            {update.change ? ` — ${changeLabel(update.change)} changed` : ""}
            {update.changedBy ? ` by ${update.changedBy}` : ""}
            {update.count > 1 ? ` (${update.count} changes)` : ""}.
          </Box>
          <Button
            onClick={handleRefresh}
            size="small"
            variant="outlined"
            startIcon={<RefreshRounded sx={{ fontSize: 16 }} />}
            sx={{ ml: "auto", py: 0.25, minHeight: 0, fontSize: 12 }}
          >
            Refresh
          </Button>
          <Tooltip title="Dismiss">
            <IconButton
              size="small"
              onClick={dismissUpdate}
              aria-label="Dismiss update"
            >
              <CloseRounded sx={{ fontSize: 16, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
